import React, { Dispatch, SetStateAction, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { IoLogOutOutline, IoPersonCircleOutline } from 'react-icons/io5';
import { paths } from '@/shared/config/paths';
import { useAuthStore } from '@store/useAuthStore';
import theme from '@styles/theme';

type GnbMenuModalProps = {
	menuModal: boolean;
	setMenuModal: Dispatch<SetStateAction<boolean>>;
}

const GnbMenuModal = ({
	menuModal,
	setMenuModal
}: GnbMenuModalProps) => {
	const navigate = useNavigate();
	const { admin, logout } = useAuthStore();
	const bgRef = useRef<HTMLDivElement>(null);
	const modalRef = useRef<HTMLDivElement>(null);
	const modalElement = document.getElementById('modal') as HTMLElement;
	
	const handleGoToProfile = () => {
		navigate(paths.my.profile.path);
		setMenuModal(false);
	}

	const handleLogout = () => {
		// 퍼블리싱 단계: 백엔드 호출 없이 클라이언트 상태만 초기화
		logout();
		navigate(paths.home.path);
	}
	
	const handleClickOutside = (e: MouseEvent) => {
		if (
			menuModal &&
			bgRef?.current?.contains(e.target as HTMLDivElement) &&
			!modalRef?.current?.contains(e.target as HTMLDivElement)
		) {
			setMenuModal(false);
		}
	}
	
	useEffect(() => {
		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	}, [menuModal]);
	
	return createPortal(
		<ModalBackground $isVisible={menuModal} ref={bgRef}>
	        <ModalContents ref={modalRef}>
		        <TabButton onClick={handleGoToProfile}>
	                <FlexBox>
	                    <IoPersonCircleOutline fontSize={20} />
	                    <Text>내 프로필</Text>
	                </FlexBox>
	                <UserName>{admin?.name || ''}</UserName>
	            </TabButton>
	            <TabButton onClick={handleLogout}>
	                <FlexBox>
	                    <IoLogOutOutline fontSize={20} color={theme.colors.activeRed} />
	                    <Logout>
	                        로그아웃
	                    </Logout>
	                </FlexBox>
	            </TabButton>
            </ModalContents>
        </ModalBackground>
		, modalElement);
};

export default GnbMenuModal;

const ModalBackground = styled.div<{ $isVisible: boolean; }>`
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 5;
  transition: .25s;
  opacity: 0;
  visibility: hidden;

  ${({ $isVisible }) => $isVisible && css`
    opacity: 1;
    z-index: 30;
    visibility: visible;
  `};
`;
const ModalContents = styled.div`
  width: 200px;
  min-height: 114px;
  position: absolute;
  z-index: 10;
  right: calc((100vw - 1280px) / 2);
  top: 60px;
  border-radius: 4px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.16);
  background-color: ${theme.colors.whiteColor};
  overflow: hidden;
`;
const TabButton = styled.div`
  padding: 16px 16px 12px;
  border-bottom: 1px solid ${theme.colors.ultraLightGrayBorder};
  cursor: pointer;

  &:last-child {
    padding: 12px 16px 16px;
    border-bottom: none;
  }

  &:hover {
    background-color: ${theme.colors.lightGrayHoverColor};
  }
`;
const FlexBox = styled.div`
  display: flex;
  align-items: center;
`;
const UserName = styled.div`
  font-size: 14px;
  color: ${theme.colors.lightGrayFontColor};
  margin-top: 8px;
`;
const Text = styled.div`
  margin-left: 12px;
`;
const Logout = styled.button`
  color: ${theme.colors.activeOrange};
  margin-left: 12px;
  cursor: pointer;
`;
